import * as React from "react";
import { ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";
import { Link } from "react-router-dom";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";
import { cn } from "~/shared/lib/utils";

export interface BreadcrumbItem {
    label: string;
    href?: string;
    id?: string;
}

export interface ChatBreadcrumbAction {
    label: string;
    onSelect?: () => void;
    icon?: React.ComponentType<{ className?: string }>;
    danger?: boolean;
    disabled?: boolean;
}

export interface ChatBreadcrumbProps {
    trail: ReadonlyArray<BreadcrumbItem>;
    actions?: ReadonlyArray<ChatBreadcrumbAction>;
    untitledLabel?: string;
    className?: string;
}

// Collapse ancestors into a "…" menu only from 4 ancestors up (i.e. a 5+ level
// trail). Our deepest chat trail is Workspace / Project / Scene / Chat = 3
// ancestors, so the project is always shown inline rather than hidden in "…".
const ANCESTOR_OVERFLOW_THRESHOLD = 4;

export function ChatBreadcrumb({
    trail,
    actions,
    untitledLabel = "Untitled chat",
    className,
}: ChatBreadcrumbProps) {
    if (trail.length === 0) {
        return (
            <div className={cn("flex items-center", className)}>
                <span className="text-xs font-semibold text-foreground">{untitledLabel}</span>
            </div>
        );
    }

    const current = trail[trail.length - 1]!;
    const ancestors = trail.slice(0, -1);
    const parent = ancestors[ancestors.length - 1];
    const overflow = ancestors.slice(0, -1);
    const hasOverflow = ancestors.length >= ANCESTOR_OVERFLOW_THRESHOLD;

    return (
        <nav aria-label="Breadcrumb" className={cn("flex min-w-0 items-center gap-1", className)}>
            <div className="flex min-w-0 flex-1 items-center gap-1 md:hidden">
                {parent && <CompactParent parent={parent} extraAncestors={overflow} />}
                <CurrentItem item={current} actions={actions} />
            </div>

            <ol className="hidden min-w-0 flex-1 items-center gap-1 md:flex">
                {overflow.length > 0 && hasOverflow ? (
                    <>
                        <AncestorLink item={ancestors[0]!} />
                        <Separator />
                        <AncestorOverflow items={overflow.slice(1)} />
                        <Separator />
                        {parent && (
                            <>
                                <AncestorLink item={parent} />
                                <Separator />
                            </>
                        )}
                    </>
                ) : (
                    ancestors.map((a, i) => (
                        <React.Fragment key={a.id ?? a.label}>
                            <AncestorLink item={a} />
                            {i < ancestors.length - 1 || (parent && ancestors.length > 0) ? (
                                <Separator />
                            ) : null}
                        </React.Fragment>
                    ))
                )}
                <CurrentItem item={current} actions={actions} />
            </ol>
        </nav>
    );
}

function Separator() {
    return (
        <li aria-hidden className="shrink-0 text-muted-foreground/60">
            <ChevronRight className="size-3.5" />
        </li>
    );
}

function AncestorLink({ item }: { item: BreadcrumbItem }) {
    const content = (
        <span className="max-w-[10rem] truncate text-xs font-medium text-muted-foreground hover:text-foreground">
            {item.label}
        </span>
    );
    return (
        <li className="flex min-w-0 items-center">
            {item.href ? (
                <Link
                    to={item.href}
                    className="-mx-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-muted/60"
                >
                    {content}
                </Link>
            ) : (
                <span className="-mx-1.5 px-1.5 py-0.5">{content}</span>
            )}
        </li>
    );
}

function AncestorOverflow({ items }: { items: ReadonlyArray<BreadcrumbItem> }) {
    if (items.length === 0) return null;
    return (
        <li className="flex shrink-0 items-center">
            <DropdownMenu>
                <DropdownMenuTrigger
                    aria-label={`Show ${items.length} hidden breadcrumb items`}
                    className="inline-flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <MoreHorizontal className="size-3.5" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-48">
                    {items.map((it) => (
                        <DropdownMenuItem key={it.id ?? it.label} asChild={!!it.href}>
                            {it.href ? (
                                <Link to={it.href} className="truncate">
                                    {it.label}
                                </Link>
                            ) : (
                                <span className="truncate">{it.label}</span>
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </li>
    );
}

function CompactParent({
    parent,
    extraAncestors,
}: {
    parent: BreadcrumbItem;
    extraAncestors: ReadonlyArray<BreadcrumbItem>;
}) {
    return (
        <>
            {parent.href ? (
                <Link
                    to={parent.href}
                    className="inline-flex max-w-[45vw] shrink items-center gap-1 truncate rounded-md px-1.5 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                    aria-label={`Back to ${parent.label}`}
                >
                    <ChevronRight
                        className="size-3.5 shrink-0 -scale-x-100 text-muted-foreground"
                        aria-hidden
                    />
                    <span className="truncate">{parent.label}</span>
                </Link>
            ) : (
                <span className="inline-flex max-w-[45vw] shrink items-center gap-1 truncate px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                    <ChevronRight className="size-3.5 shrink-0 -scale-x-100" aria-hidden />
                    <span className="truncate">{parent.label}</span>
                </span>
            )}
            {extraAncestors.length > 0 && <AncestorOverflow items={extraAncestors} />}
            <ChevronRight className="size-3.5 shrink-0 text-muted-foreground/60" aria-hidden />
        </>
    );
}

function CurrentItem({ item, actions }: { item: BreadcrumbItem; actions?: ChatBreadcrumbProps["actions"] }) {
    const label = <span className="truncate text-xs font-semibold text-foreground">{item.label}</span>;
    if (!actions || actions.length === 0) {
        return (
            <li className="min-w-0 flex-1">
                {item.href ? (
                    <Link
                        to={item.href}
                        className="-mx-1.5 inline-flex max-w-full min-w-0 items-center rounded-md px-1.5 py-0.5 transition-colors hover:bg-muted/60"
                    >
                        {label}
                    </Link>
                ) : (
                    <div className="-mx-1.5 min-w-0 px-1.5 py-0.5">{label}</div>
                )}
            </li>
        );
    }
    return (
        <li className="min-w-0 flex-1">
            <DropdownMenu>
                <DropdownMenuTrigger
                    aria-label={`${item.label} — actions`}
                    className="group -mx-1.5 inline-flex max-w-full min-w-0 items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-muted/60"
                >
                    {label}
                    <ChevronDown
                        className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-foreground"
                        aria-hidden
                    />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-40">
                    {actions.map((a) => (
                        <DropdownMenuItem
                            key={a.label}
                            onSelect={a.onSelect}
                            disabled={a.disabled}
                            className={cn(a.danger && "text-destructive focus:text-destructive")}
                        >
                            {a.icon && <a.icon className="mr-2 size-4" />}
                            {a.label}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        </li>
    );
}
